import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { io } from "socket.io-client";
import { APP_NAME, CHAT_THEME } from "../../util/brand";
import axios from "axios";
import customAxios from "../../util/customAxios";
import { UserContext } from "../../context/user";
import { allowsCategory } from "../../util/cookieConsent";
import { CHAT_OPENING_MESSAGES } from "../../util/chatOpeners";

const FALLBACK_API_BASE = `${window.location.origin}/api`;
const API_URL = import.meta.env.VITE_API_URL || FALLBACK_API_BASE;
const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `wv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId() {
  if (allowsCategory("functional")) {
    let id = localStorage.getItem("wv_chat_session");
    if (!id) {
      id = generateId();
      localStorage.setItem("wv_chat_session", id);
    }
    return id;
  }
  if (!window.__wv_chat_ephemeral) {
    window.__wv_chat_ephemeral = generateId();
  }
  return window.__wv_chat_ephemeral;
}

function isImageUrl(text) {
  return (
    /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(text) ||
    text.includes("/api/report-screenshots/")
  );
}

const styles = getStyles(CHAT_THEME);

export default function ChatWidget() {
  const { user } = useContext(UserContext);
  const canAttachScreenshot = Boolean(user?.token);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [bugLabel, setBugLabel] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const sessionId = getSessionId();
    const socket = io(SOCKET_URL, {
      query: { sessionId },
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("chat:visitor_context", { url: window.location.href });
      if (user?.token) {
        socket.emit("chat:auth", { token: user.token });
      }
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on("chat:message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      setUnread((prev) => prev + 1);
    });

    socketRef.current = socket;
    return () => socket.disconnect();
  }, [user?.token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, pendingImage, scrollToBottom]);

  useEffect(() => {
    if (!open) return;
    setUnread(0);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const emitMessage = (text, label = "") => {
    if (!text || !socketRef.current) return;
    socketRef.current.emit("chat:send", { text, bugLabel: label });
    setMessages((prev) => [
      ...prev,
      {
        text: label ? `[Bug: ${label}]` : text,
        from: "visitor",
        timestamp: Date.now(),
        attachmentUrl: isImageUrl(text) ? text : "",
        bugLabel: label,
      },
    ]);
  };

  const sendText = (text) => {
    emitMessage(text);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    sendText(text);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearPendingImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setPendingImage(null);
    setImagePreview(null);
    setBugLabel("");
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (!canAttachScreenshot) return;
    setPendingImage(file);
    setImagePreview(URL.createObjectURL(file));
    setBugLabel("");
  };

  const sendPendingImage = async () => {
    const label = bugLabel.trim();
    if (!pendingImage || !label) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", pendingImage);
      const res = await customAxios.post(`${API_URL}/basic/chat-screenshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.url) {
        emitMessage(res.data.url, label);
        clearPendingImage();
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = (e) => {
    if (!canAttachScreenshot) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        break;
      }
    }
  };

  const renderMessageContent = (msg) => {
    const text = msg.text || "";
    const attachment = msg.attachmentUrl || (isImageUrl(text) ? text : "");
    if (attachment) {
      return (
        <a href={attachment} target="_blank" rel="noopener noreferrer">
          {msg.bugLabel && (
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.9 }}>
              Bug: {msg.bugLabel}
            </div>
          )}
          <img
            src={attachment}
            alt="Shared screenshot"
            style={{
              maxWidth: "100%",
              maxHeight: 180,
              borderRadius: 8,
              display: "block",
            }}
          />
        </a>
      );
    }
    return text;
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const headerSubtitle = connected
    ? "We typically reply in minutes"
    : "Connecting...";

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={styles.trigger}
          aria-label="Open chat"
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          {unread > 0 && <span style={styles.badge}>{unread}</span>}
        </button>
      )}

      {open && (
        <div
          style={styles.panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wv-chat-title"
          aria-describedby="wv-chat-desc"
        >
          <div style={styles.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={styles.avatar} aria-hidden="true">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <div>
                <div id="wv-chat-title" style={styles.headerTitle}>{APP_NAME} support chat</div>
                <div id="wv-chat-desc" style={styles.headerSub}>{headerSubtitle}</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} style={styles.closeBtn} aria-label="Close chat">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div ref={scrollRef} style={styles.messages} aria-live="polite" aria-relevant="additions">
            {messages.length === 0 && !pendingImage && CHAT_OPENING_MESSAGES.map((text, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginBottom: 8,
                }}
              >
                <div style={{ ...styles.bubble, ...styles.bubbleReply }}>
                  <div style={styles.replyLabel}>VideoBrief</div>
                  {text}
                </div>
              </div>
            ))}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.from === "visitor" ? "flex-end" : "flex-start",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    ...styles.bubble,
                    ...(msg.from === "visitor" ? styles.bubbleVisitor : styles.bubbleReply),
                  }}
                >
                  {msg.from === "human" && (
                    <div style={styles.replyLabel}>Brian</div>
                  )}
                  {msg.from === "ai" && (
                    <div style={styles.replyLabel}>AI Assistant</div>
                  )}
                  {renderMessageContent(msg)}
                </div>
              </div>
            ))}
            {pendingImage && (
              <div style={{ marginBottom: 8, padding: 10, background: "#f9fafb", borderRadius: 12 }}>
                <img
                  src={imagePreview}
                  alt="Screenshot preview"
                  style={{ maxWidth: "100%", maxHeight: 120, borderRadius: 8, marginBottom: 8 }}
                />
                <input
                  value={bugLabel}
                  onChange={(e) => setBugLabel(e.target.value)}
                  placeholder="Label this bug (required)"
                  style={{ ...styles.input, width: "100%", marginBottom: 8 }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={clearPendingImage} style={styles.secondaryBtn}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!bugLabel.trim() || uploading}
                    onClick={sendPendingImage}
                    style={{
                      ...styles.sendBtn,
                      width: "auto",
                      borderRadius: 8,
                      padding: "8px 14px",
                      opacity: bugLabel.trim() && !uploading ? 1 : 0.4,
                    }}
                  >
                    {uploading ? "Sending…" : "Send screenshot"}
                  </button>
                </div>
              </div>
            )}
            {uploading && !pendingImage && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <div style={{ ...styles.bubble, ...styles.bubbleVisitor, opacity: 0.7 }}>
                  Uploading image...
                </div>
              </div>
            )}
          </div>

          {!pendingImage && (
            <div style={styles.inputArea} onPaste={handlePaste}>
              {canAttachScreenshot && (
                <>
                  <input
                    type="file"
                    ref={fileRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    style={styles.attachBtn}
                    aria-label="Attach screenshot"
                    disabled={uploading}
                  >
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                </>
              )}
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tell us what you're trying to do with B2B video…"
                style={styles.input}
                aria-label="Message to support"
              />
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || uploading}
                style={{
                  ...styles.sendBtn,
                  opacity: input.trim() && !uploading ? 1 : 0.4,
                }}
                aria-label="Send message"
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function getStyles(theme) {
  return {
  trigger: {
    position: "fixed",
    bottom: 24,
    left: 24,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 4px 20px ${theme.accentShadow}`,
    zIndex: 9999,
    transition: "transform 0.15s ease",
  },
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    background: "#ef4444",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    position: "fixed",
    bottom: 24,
    left: 24,
    width: 380,
    maxWidth: "calc(100vw - 32px)",
    height: 520,
    maxHeight: "calc(100vh - 48px)",
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    background: theme.accent,
    color: "#fff",
    padding: "16px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
  },
  headerSub: {
    fontSize: 12,
    opacity: 0.85,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    opacity: 0.8,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    minHeight: 0,
  },
  welcome: {
    textAlign: "center",
    padding: "32px 16px",
  },
  bubble: {
    maxWidth: "80%",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  bubbleVisitor: {
    background: theme.accent,
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  bubbleReply: {
    background: "#f3f4f6",
    color: "#1f2937",
    borderBottomLeftRadius: 4,
  },
  replyLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 2,
  },
  inputArea: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "12px 12px 12px 8px",
    borderTop: "1px solid #e5e7eb",
    flexShrink: 0,
    background: "#fff",
  },
  attachBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "none",
    color: "#6b7280",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "color 0.15s",
  },
  input: {
    flex: 1,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  },
  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  secondaryBtn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    cursor: "pointer",
  },
  };
}
